/**
 * Mirrors the Java ServiceUtil retry pattern:
 *   Retry.fixedDelay(2, Duration.ofSeconds(2))
 *     .filter(error -> !isForStatusCode(error, noRetry))
 *     .onRetryExhaustedThrow(...)
 *
 * Retries up to FIXED_RETRIES times with FIXED_DELAY_MS between each attempt.
 * An error is NOT retried if its HTTP status code is present in the noRetry set
 * (equivalent to the Java noRetry Set<HttpStatusCode> guard).
 * When all retries are exhausted a RetryExhaustedException is thrown.
 */

const FIXED_RETRIES  = 2;           // matches fixedDelay(2, …) — 2 retries = 3 total attempts
const FIXED_DELAY_MS = 2_000;       // matches Duration.ofSeconds(2)

/** Thrown when all retry attempts are exhausted — mirrors RetryException("Retry exhausted.") */
export class RetryExhaustedException extends Error {
  constructor(url: string, cause?: unknown) {
    super(`Retry exhausted after ${FIXED_RETRIES} retries for ${url}`);
    this.name = 'RetryExhaustedException';
    if (cause && this.stack) {
      this.stack += `\nCaused by: ${cause instanceof Error ? cause.stack : String(cause)}`;
    }
  }
}

/** Wraps a non-2xx HTTP response as a throwable — mirrors WebClientResponseException */
export class HttpStatusError extends Error {
  constructor(
    public readonly status: number,
    public readonly response: Response,
    message: string,
  ) {
    super(message);
    this.name = 'HttpStatusError';
  }
}

/** Mirrors: error instanceof WebClientResponseException && codes.contains(statusCode) */
function isForStatusCode(error: unknown, noRetry: Set<number>): boolean {
  return error instanceof HttpStatusError && noRetry.has(error.status);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * fetchWithRetry — TypeScript port of ServiceUtil.request()
 *
 * @param url       - Resource URL
 * @param options   - Standard RequestInit options (signal managed internally)
 * @param noRetry   - HTTP status codes that should NOT trigger a retry (default: empty)
 * @param timeoutMs - Per-attempt client-side abort timeout in ms (default: 60 000)
 */
export async function fetchWithRetry(
  url: string,
  options: Omit<RequestInit, 'signal'> = {},
  noRetry: Set<number> = new Set(),
  timeoutMs = 60_000,
): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= FIXED_RETRIES; attempt++) {
    // Fixed delay before every retry (not before the first attempt)
    if (attempt > 0) {
      await sleep(FIXED_DELAY_MS);
      console.warn(
        `[fetchWithRetry] Retry ${attempt}/${FIXED_RETRIES} for ${url}…`
      );
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);

      if (!response.ok) {
        const err = new HttpStatusError(response.status, response, `HTTP ${response.status}`);

        // Mirrors: .filter(error -> !isForStatusCode(error, noRetry))
        // If the status is in noRetry → do NOT retry, return the response immediately
        if (isForStatusCode(err, noRetry)) {
          return response;
        }

        // Otherwise treat as a retryable error
        console.error(`[fetchWithRetry] Not a success response! HTTP ${response.status} from ${url}`);
        lastError = err;
        continue; // go to next attempt
      }

      return response; // 2xx success
    } catch (err: any) {
      clearTimeout(timer);

      // Mirrors: .filter(error -> !isForStatusCode(error, noRetry))
      if (isForStatusCode(err, noRetry)) {
        throw err;
      }

      console.error(`[fetchWithRetry] Not a success response!`, err);
      lastError = err;
      // continue to next attempt
    }
  }

  // Mirrors: .onRetryExhaustedThrow(...)
  console.error(`[fetchWithRetry] Retries expended while trying to make request to ${url}`);
  throw new RetryExhaustedException(url, lastError);
}
